import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Rocket, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  batchDeleteInvoices,
  checkInvoices,
  createInvoiceIn1C,
  deleteInvoice,
  listInvoices,
  uploadInvoice,
  verifyReadiness,
} from "@/api/invoices";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  aggregateMissingMappings,
  countReadiness,
} from "@/lib/batchAggregation";
import { formatRub, formatRuDate, formatRuDateTime } from "@/lib/format";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import {
  BatchCreateConfirmDialog,
} from "@/pages/invoices/BatchCreateConfirmDialog";
import {
  BatchProgressPanel,
  type BatchResult,
} from "@/pages/invoices/BatchProgressPanel";
import {
  DuplicateConfirmDialog,
  type DuplicateEntry,
} from "@/pages/invoices/DuplicateConfirmDialog";
import { FileDropzone } from "@/pages/invoices/FileDropzone";
import { InvoiceStatusChip } from "@/pages/invoices/InvoiceStatusChip";
import { MissingKontragentsPanel } from "@/pages/invoices/MissingKontragentsPanel";
import { MissingMappingsPanel } from "@/pages/invoices/MissingMappingsPanel";
import {
  type PendingRow,
  type RowStatus,
  UploadResultsPanel,
} from "@/pages/invoices/UploadResultsPanel";
import type { DuplicateInfo, InvoiceSummary } from "@/types/api";
import { useConnection } from "@/auth/ConnectionContext";

type ListFilter = "all" | "ready" | "attention" | "created";

function filterInvoices(
  invoices: InvoiceSummary[],
  filter: ListFilter,
): InvoiceSummary[] {
  if (filter === "all") return invoices;
  if (filter === "ready") return invoices.filter((i) => i.readiness === "ready");
  if (filter === "created")
    return invoices.filter((i) => i.readiness === "created");
  // attention = needs_nomenclature + incomplete_mapping + parse_error
  return invoices.filter(
    (i) =>
      i.readiness === "needs_nomenclature" ||
      i.readiness === "incomplete_mapping" ||
      i.readiness === "parse_error",
  );
}

/** Row kept alongside PendingRow that carries the original File for upload. */
interface InternalRow extends PendingRow {
  file?: File;
  duplicate?: DuplicateInfo | null;
  document_number?: string | null;
}

export function InvoicesPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { activeConnectionId: connId } = useConnection();
  const [rows, setRows] = useState<InternalRow[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Ref mirrors `rows` so callbacks can read latest state without stale
  // closures, and — critically — so we can schedule uploads OUTSIDE of a
  // setRows updater function. React StrictMode runs updaters twice; if
  // `runUploads()` is called from inside one, it fires twice → each file
  // gets uploaded twice.
  const rowsRef = useRef<InternalRow[]>([]);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const listQuery = useQuery({ queryKey: ["invoices", connId], queryFn: listInvoices });
  const verifyQuery = useQuery({
    queryKey: ["invoices-verify", connId],
    queryFn: verifyReadiness,
    enabled: !!listQuery.data && listQuery.data.length > 0,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  const handleFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    // 1. Initialize panel with checking status.
    const initial: InternalRow[] = files.map((f) => ({
      filename: f.name,
      status: "checking" as RowStatus,
      message: "Проверяем файл…",
      file: f,
    }));
    setRows(initial);

    // 2. Call /check for the whole batch.
    let results;
    try {
      results = await checkInvoices(files);
    } catch (err) {
      const msg =
        err instanceof AxiosError
          ? (err.response?.data?.detail ?? "Ошибка проверки")
          : "Ошибка проверки";
      setRows((prev) =>
        prev.map((r) => ({
          ...r,
          status: "error",
          message: msg,
        })),
      );
      toast.error(`Не удалось проверить файлы: ${msg}`);
      return;
    }

    // 3. Update rows with check results.
    const updated: InternalRow[] = files.map((f, i) => {
      const check = results[i];
      if (!check) {
        return {
          filename: f.name,
          status: "error",
          message: "Нет ответа сервера",
          file: f,
        };
      }
      if (check.parse_error) {
        return {
          filename: f.name,
          status: "error",
          message: check.parse_error,
          file: f,
        };
      }
      if (check.is_duplicate && check.duplicate) {
        return {
          filename: f.name,
          status: "duplicate",
          message: `Уже загружен ${formatRuDate(check.duplicate.uploaded_at)} как «${check.duplicate.filename}»`,
          file: f,
          duplicate: check.duplicate,
          document_number: check.document_number,
        };
      }
      return {
        filename: f.name,
        status: "ready",
        message: check.document_number
          ? `Готов к загрузке · № ${check.document_number}`
          : "Готов к загрузке",
        file: f,
        document_number: check.document_number,
      };
    });
    setRows(updated);

    // 4. Any duplicates? Ask the user what to do.
    const hasDuplicates = updated.some((r) => r.status === "duplicate");
    if (hasDuplicates) {
      setConfirmOpen(true);
      return; // upload will be triggered after user chooses
    }

    // 5. No duplicates → upload the "ready" ones directly.
    await runUploads(updated);
  }, []);

  const runUploads = useCallback(
    async (currentRows: InternalRow[]) => {
      const toUpload = currentRows.filter((r) => r.status === "ready");
      if (toUpload.length === 0) {
        summaryToast(currentRows);
        queryClient.invalidateQueries({ queryKey: ["invoices", connId] }); queryClient.invalidateQueries({ queryKey: ["invoices-verify", connId] });
        return;
      }

      // Mark uploading (updater must be pure — no side effects inside).
      setRows((prev) =>
        prev.map((r) =>
          r.status === "ready"
            ? { ...r, status: "uploading", message: "Загружаем…" }
            : r,
        ),
      );

      // Track outcomes locally. Source of truth for the final summary is the
      // returned snapshot from this loop, not React state — so we don't need
      // to read state after commits (and we don't accidentally fire side
      // effects inside a setRows updater).
      type Outcome = { status: "uploaded" | "error"; message: string };
      const outcomes = new Map<string, Outcome>();

      const CONCURRENCY = 3;
      const queue = [...toUpload];
      const worker = async () => {
        while (queue.length > 0) {
          const row = queue.shift();
          if (!row || !row.file) continue;
          try {
            const detail = await uploadInvoice(row.file);
            const outcome: Outcome =
              detail.status === "parsed"
                ? {
                    status: "uploaded",
                    message: `Загружено · № ${detail.document_number ?? "—"}`,
                  }
                : {
                    status: "error",
                    message: `Сохранено с ошибкой: ${detail.parse_error ?? ""}`,
                  };
            outcomes.set(row.filename, outcome);
            setRows((prev) =>
              prev.map((r) =>
                r.filename === row.filename && r.status === "uploading"
                  ? { ...r, status: outcome.status, message: outcome.message }
                  : r,
              ),
            );
          } catch (err) {
            let msg = "Ошибка загрузки";
            if (err instanceof AxiosError) {
              const code = err.response?.status;
              const detail = err.response?.data?.detail;
              if (code === 415) msg = detail ?? "Неподдерживаемый тип файла";
              else if (code === 413) msg = detail ?? "Файл слишком большой";
              else msg = detail ?? msg;
            }
            outcomes.set(row.filename, { status: "error", message: msg });
            setRows((prev) =>
              prev.map((r) =>
                r.filename === row.filename && r.status === "uploading"
                  ? { ...r, status: "error", message: msg }
                  : r,
              ),
            );
          }
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, queue.length) }, () => worker()),
      );

      // Build final snapshot for summary without reading React state.
      const finalRows = currentRows.map((r) => {
        const outcome = outcomes.get(r.filename);
        if (r.status === "ready" && outcome) {
          return { ...r, status: outcome.status };
        }
        return r;
      });
      summaryToast(finalRows);
      queryClient.invalidateQueries({ queryKey: ["invoices", connId] }); queryClient.invalidateQueries({ queryKey: ["invoices-verify", connId] });
    },
    [queryClient],
  );

  const onSkipDuplicates = useCallback(async () => {
    setConfirmOpen(false);
    const next = rowsRef.current.map((r) =>
      r.status === "duplicate"
        ? {
            ...r,
            status: "skipped" as RowStatus,
            message: r.duplicate
              ? `Пропущен — уже загружен ${formatRuDate(r.duplicate.uploaded_at)}`
              : "Пропущен как дубликат",
          }
        : r,
    );
    setRows(next);
    await runUploads(next);
  }, [runUploads]);

  const onForceUpload = useCallback(async () => {
    setConfirmOpen(false);
    const next = rowsRef.current.map((r) =>
      r.status === "duplicate"
        ? {
            ...r,
            status: "ready" as RowStatus,
            message: r.document_number
              ? `Готов к загрузке · № ${r.document_number}`
              : "Готов к загрузке",
          }
        : r,
    );
    setRows(next);
    await runUploads(next);
  }, [runUploads]);

  const onCancelUpload = useCallback(() => {
    setConfirmOpen(false);
    setRows([]);
  }, []);

  const deleteMutation = useMutation({
    mutationFn: deleteInvoice,
    onSuccess: () => {
      toast.success("Удалено");
      queryClient.invalidateQueries({ queryKey: ["invoices", connId] }); queryClient.invalidateQueries({ queryKey: ["invoices-verify", connId] });
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e, "Не удалось удалить")),
  });

  const handleDelete = (id: string, label: string) => {
    if (!confirm(`Удалить запись «${label}»? Файл будет удалён безвозвратно.`)) return;
    deleteMutation.mutate(id);
  };

  // --- Stage 6.3: batch selection, filtering, bulk actions ---
  const [filter, setFilter] = useState<ListFilter>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmCreateOpen, setConfirmCreateOpen] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchResults, setBatchResults] = useState<BatchResult[]>([]);
  const [batchTotal, setBatchTotal] = useState(0);
  const cancelRequestedRef = useRef(false);

  // Overlay verified readiness on top of the tentative (DB-only) list.
  const invoices = useMemo(() => {
    const raw = listQuery.data ?? [];
    if (!verifyQuery.data) return raw;
    const verifiedMap = new Map(
      verifyQuery.data.statuses.map((v) => [v.id, v]),
    );
    return raw.map((inv) => {
      const v = verifiedMap.get(inv.id);
      if (!v) return inv;
      return { ...inv, readiness: v.readiness };
    });
  }, [listQuery.data, verifyQuery.data]);

  const missingKontragents = verifyQuery.data?.missing_kontragents ?? [];
  const filteredInvoices = useMemo(
    () => filterInvoices(invoices, filter),
    [invoices, filter],
  );
  const counts = useMemo(() => countReadiness(invoices), [invoices]);
  const missingGroups = useMemo(
    () => aggregateMissingMappings(invoices),
    [invoices],
  );

  // Keep selection consistent when the list changes (e.g. after delete).
  useEffect(() => {
    const existingIds = new Set(invoices.map((i) => i.id));
    setSelectedIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) if (existingIds.has(id)) next.add(id);
      return next;
    });
  }, [invoices]);

  const selectedInvoices = useMemo(
    () => invoices.filter((i) => selectedIds.has(i.id)),
    [invoices, selectedIds],
  );
  const selectedReadyCount = selectedInvoices.filter(
    (i) => i.readiness === "ready",
  ).length;

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const visibleIds = filteredInvoices.map((i) => i.id);
      const allSelected = visibleIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        for (const id of visibleIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of visibleIds) next.add(id);
      return next;
    });
  };

  const batchDeleteMutation = useMutation({
    mutationFn: batchDeleteInvoices,
    onSuccess: () => {
      toast.success(`Удалено ${selectedIds.size}`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ["invoices", connId] }); queryClient.invalidateQueries({ queryKey: ["invoices-verify", connId] });
    },
    onError: (err) => {
      const msg =
        err instanceof AxiosError
          ? (err.response?.data?.detail ?? "Не удалось удалить")
          : "Не удалось удалить";
      toast.error(msg);
    },
  });

  const handleBatchDelete = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (
      !confirm(
        `Удалить ${ids.length} ${ids.length === 1 ? "файл" : "файла(ов)"}? Действие необратимо.`,
      )
    )
      return;
    batchDeleteMutation.mutate(ids);
  };

  const runBatchCreate = useCallback(async () => {
    const ready = selectedInvoices.filter((i) => i.readiness === "ready");
    if (ready.length === 0) {
      toast.info("Нет готовых файлов в выделении");
      return;
    }
    setConfirmCreateOpen(false);
    setBatchRunning(true);
    setBatchResults([]);
    setBatchTotal(ready.length);
    cancelRequestedRef.current = false;

    for (const inv of ready) {
      if (cancelRequestedRef.current) break;
      try {
        const created = await createInvoiceIn1C(inv.id);
        setBatchResults((prev) => [
          ...prev,
          {
            invoice_id: inv.id,
            filename: inv.filename,
            ok: true,
            postuplenie_number: created.postuplenie_number,
          },
        ]);
      } catch (err) {
        let msg = "Ошибка создания";
        if (err instanceof AxiosError) {
          msg = err.response?.data?.detail ?? err.message ?? msg;
        }
        setBatchResults((prev) => [
          ...prev,
          {
            invoice_id: inv.id,
            filename: inv.filename,
            ok: false,
            error: msg,
          },
        ]);
      }
    }

    setBatchRunning(false);
    queryClient.invalidateQueries({ queryKey: ["invoices", connId] }); queryClient.invalidateQueries({ queryKey: ["invoices-verify", connId] });
  }, [queryClient, selectedInvoices]);

  const handleBatchCreateClick = () => {
    if (selectedInvoices.length === 0) return;
    setConfirmCreateOpen(true);
  };

  const duplicateEntries: DuplicateEntry[] = rows
    .filter((r) => r.status === "duplicate" && r.duplicate)
    .map((r) => ({
      filename: r.filename,
      document_number: r.document_number ?? null,
      duplicate: r.duplicate!,
    }));

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Счета-фактуры</h1>

      <FileDropzone
        onFiles={handleFiles}
        disabled={rows.some(
          (r) => r.status === "checking" || r.status === "uploading",
        )}
      />

      {rows.length > 0 && (
        <UploadResultsPanel rows={rows} onClose={() => setRows([])} />
      )}

      <DuplicateConfirmDialog
        open={confirmOpen}
        duplicates={duplicateEntries}
        onSkip={onSkipDuplicates}
        onForce={onForceUpload}
        onCancel={onCancelUpload}
      />

      {verifyQuery.isLoading && invoices.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Проверяю соответствия в 1С…
        </div>
      )}

      {missingKontragents.length > 0 && (
        <MissingKontragentsPanel kontragents={missingKontragents} />
      )}

      {missingGroups.length > 0 && (
        <MissingMappingsPanel groups={missingGroups} />
      )}

      {(batchRunning || batchResults.length > 0) && (
        <BatchProgressPanel
          isRunning={batchRunning}
          total={batchTotal}
          results={batchResults}
          cancelRequested={cancelRequestedRef.current}
          onCancel={() => {
            cancelRequestedRef.current = true;
          }}
          onClose={() => {
            setBatchResults([]);
            setBatchTotal(0);
          }}
        />
      )}

      <BatchCreateConfirmDialog
        open={confirmCreateOpen}
        invoices={selectedInvoices}
        onConfirm={runBatchCreate}
        onClose={() => setConfirmCreateOpen(false)}
      />

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">История загрузок</h2>
          {invoices.length > 0 && (
            <div className="text-xs text-muted-foreground">
              Всего {counts.total} ·{" "}
              <span className="text-green-700">🟢 {counts.ready} готовы</span> ·{" "}
              <span className="text-amber-700">
                🟡 {counts.needs_nomenclature + counts.incomplete_mapping} требуют внимания
              </span>{" "}
              ·{" "}
              <span className="text-green-800">✅ {counts.created} созданы</span>
              {counts.parse_error > 0 && (
                <> · <span className="text-destructive">🔴 {counts.parse_error} ошибка</span></>
              )}
            </div>
          )}
        </div>

        {listQuery.isLoading && (
          <p className="text-sm text-muted-foreground">Загрузка…</p>
        )}
        {listQuery.isError && (
          <p className="text-sm text-destructive">
            Не удалось загрузить список: {extractErrorMessage(listQuery.error, "нет ответа")}
          </p>
        )}
        {listQuery.data && listQuery.data.length === 0 && (
          <p className="text-sm text-muted-foreground">Пока ничего не загружено</p>
        )}

        {listQuery.data && listQuery.data.length > 0 && (
          <>
            <FilterTabs filter={filter} counts={counts} onChange={setFilter} />
            {selectedIds.size > 0 && (
              <BatchActionsBar
                selectedCount={selectedIds.size}
                readyCount={selectedReadyCount}
                onBatchCreate={handleBatchCreateClick}
                onBatchDelete={handleBatchDelete}
                onClear={() => setSelectedIds(new Set())}
                busy={batchRunning || batchDeleteMutation.isPending}
              />
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={
                        filteredInvoices.length > 0 &&
                        filteredInvoices.every((i) => selectedIds.has(i.id))
                      }
                      onCheckedChange={toggleAllVisible}
                      aria-label="Выделить все"
                    />
                  </TableHead>
                  <TableHead className="w-36">Статус</TableHead>
                  <TableHead>Файл</TableHead>
                  <TableHead>Номер</TableHead>
                  <TableHead>Дата</TableHead>
                  <TableHead>Продавец</TableHead>
                  <TableHead>Организация</TableHead>
                  <TableHead>Составитель</TableHead>
                  <TableHead className="text-right">Сумма</TableHead>
                  <TableHead className="text-right">Загружено</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map((inv) => (
                  <InvoiceRow
                    key={inv.id}
                    invoice={inv}
                    selected={selectedIds.has(inv.id)}
                    onToggleSelect={() => toggleRow(inv.id)}
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                    onDelete={(e) => {
                      e.stopPropagation();
                      handleDelete(inv.id, inv.filename);
                    }}
                  />
                ))}
              </TableBody>
            </Table>
            {filteredInvoices.length === 0 && (
              <p className="mt-3 text-sm text-muted-foreground">
                В этом фильтре ничего нет.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function FilterTabs({
  filter,
  counts,
  onChange,
}: {
  filter: ListFilter;
  counts: ReturnType<typeof countReadiness>;
  onChange: (f: ListFilter) => void;
}): React.ReactElement {
  const tabs: { key: ListFilter; label: string; count: number }[] = [
    { key: "all", label: "Все", count: counts.total },
    { key: "ready", label: "Готовые", count: counts.ready },
    {
      key: "attention",
      label: "Требуют внимания",
      count:
        counts.needs_nomenclature +
        counts.incomplete_mapping +
        counts.parse_error,
    },
    { key: "created", label: "Созданы", count: counts.created },
  ];
  return (
    <div className="mb-3 flex flex-wrap gap-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={cn(
            "rounded-md border px-3 py-1 text-sm transition-colors",
            filter === t.key
              ? "border-primary bg-primary/10 text-primary"
              : "border-border hover:bg-accent/50",
          )}
        >
          {t.label}
          <span className="ml-2 text-xs text-muted-foreground">{t.count}</span>
        </button>
      ))}
    </div>
  );
}

function BatchActionsBar({
  selectedCount,
  readyCount,
  onBatchCreate,
  onBatchDelete,
  onClear,
  busy,
}: {
  selectedCount: number;
  readyCount: number;
  onBatchCreate: () => void;
  onBatchDelete: () => void;
  onClear: () => void;
  busy: boolean;
}): React.ReactElement {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border bg-muted/30 p-3 text-sm">
      <span className="font-medium">Выбрано: {selectedCount}</span>
      {readyCount !== selectedCount && (
        <span className="text-xs text-muted-foreground">
          ({readyCount} готовы к созданию)
        </span>
      )}
      <div className="ml-auto flex gap-2">
        <Button
          size="sm"
          onClick={onBatchCreate}
          disabled={readyCount === 0 || busy}
          className="gap-2"
        >
          <Rocket className="size-4" />
          Создать в 1С ({readyCount})
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onBatchDelete}
          disabled={busy}
          className="gap-2"
        >
          <Trash2 className="size-4" />
          Удалить ({selectedCount})
        </Button>
        <Button size="sm" variant="ghost" onClick={onClear} disabled={busy}>
          Снять выделение
        </Button>
      </div>
    </div>
  );
}

function summaryToast(rows: InternalRow[]): void {
  const uploaded = rows.filter((r) => r.status === "uploaded").length;
  const skipped = rows.filter((r) => r.status === "skipped").length;
  const errors = rows.filter((r) => r.status === "error").length;

  const parts: string[] = [];
  if (uploaded > 0) parts.push(`${uploaded} ${pluralize(uploaded, "загружен", "загружено", "загружено")}`);
  if (skipped > 0) parts.push(`${skipped} пропущено как ${skipped === 1 ? "дубликат" : "дубликаты"}`);
  if (errors > 0) parts.push(`${errors} с ошибкой`);

  if (parts.length === 0) return;
  const msg = parts.join(", ");
  if (errors > 0 && uploaded === 0) toast.error(`Готово: ${msg}`);
  else if (uploaded > 0) toast.success(`Готово: ${msg}`);
  else toast.info(`Готово: ${msg}`);
}

function pluralize(n: number, one: string, few: string, many: string): string {
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return many;
  const last = n % 10;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}

function InvoiceRow({
  invoice,
  selected,
  onToggleSelect,
  onClick,
  onDelete,
}: {
  invoice: InvoiceSummary;
  selected: boolean;
  onToggleSelect: () => void;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}): React.ReactElement {
  const toneBg =
    invoice.readiness === "parse_error"
      ? "bg-destructive/5"
      : invoice.readiness === "created"
        ? "bg-green-500/5"
        : invoice.readiness === "ready"
          ? "bg-green-500/[0.03]"
          : invoice.readiness === "needs_nomenclature" ||
              invoice.readiness === "incomplete_mapping"
            ? "bg-amber-500/[0.05]"
            : undefined;
  return (
    <TableRow
      className={cn("cursor-pointer", toneBg)}
      onClick={onClick}
      title={invoice.parse_error ?? undefined}
    >
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={selected}
          onCheckedChange={onToggleSelect}
          aria-label="Выбрать"
        />
      </TableCell>
      <TableCell>
        <InvoiceStatusChip
          readiness={invoice.readiness}
          createdNumber={invoice.created_postuplenie_number}
        />
      </TableCell>
      <TableCell className="max-w-[180px] truncate font-medium">
        {invoice.filename}
      </TableCell>
      <TableCell>{invoice.document_number ?? "—"}</TableCell>
      <TableCell>{formatRuDate(invoice.document_date)}</TableCell>
      <TableCell className="max-w-[180px] text-muted-foreground">
        <div className="truncate">{invoice.seller_name ?? "—"}</div>
        {invoice.seller_inn && (
          <div className="text-xs opacity-70">ИНН {invoice.seller_inn}</div>
        )}
      </TableCell>
      <TableCell className="max-w-[180px] text-muted-foreground">
        <div className="truncate">{invoice.buyer_name ?? "—"}</div>
        {invoice.buyer_inn && (
          <div className="text-xs opacity-70">ИНН {invoice.buyer_inn}</div>
        )}
      </TableCell>
      <TableCell className="max-w-[180px] text-muted-foreground">
        <div className="truncate">{invoice.creator_name ?? "—"}</div>
        {invoice.creator_inn && (
          <div className="text-xs opacity-70">
            ИНН {invoice.creator_inn}
            {invoice.creator_kpp ? ` / КПП ${invoice.creator_kpp}` : ""}
          </div>
        )}
      </TableCell>
      <TableCell className="text-right font-medium">
        {formatRub(invoice.total_with_vat)}
      </TableCell>
      <TableCell className="text-right text-xs text-muted-foreground">
        {formatRuDateTime(invoice.uploaded_at)}
      </TableCell>
      <TableCell>
        <Button variant="ghost" size="icon" onClick={onDelete}>
          <Trash2 className="size-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
